import React, { useEffect, useState, useRef, useMemo } from "react";
import Modal from 'react-modal';
import { useNavigate, useLocation } from "react-router-dom";
import icons from './data/icons';
import api from "./api/api";
// import {
//   labelMap,
//   sectionTitles,
// } from "./data/Projects";

import {
  getPortfolio,
  getPublications,
  getCommunity
} from "./api/portfolio.service";
import { DivideIcon } from "lucide-react";

export const labelMap = {
  project: 'Projects',
  publication: 'Publications',
  community: 'Community',
  services: "Services",
};
export const sectionTitles = {
  project: {
    main: 'Featured',
    highlight: 'Projects',
    subtitle: 'Explore my projects and innovations'
  },
  publication: {
    main: 'Research',
    highlight: 'Publications',
    subtitle: 'Research contributions and publications'
  },
  community: {
    main: 'Community',
    highlight: 'Engagement',
    subtitle: 'Making a positive impact through community engagement'
  },
};

// ===== Helper functions (unchanged) =====
const getTechIcon = (tech) => {
  if (!tech) return null;

  const isObject = typeof tech === "object" && tech !== null;

  const iconName = isObject
    ? tech.icon?.toString().trim()
    : tech.toString().trim();

  const iconColor = isObject && tech.color
    ? tech.color
    : "#236dd4";

  if (!iconName) return null;

  // Direct icon name
  if (icons[iconName]) {
    const IconComponent = icons[iconName];

    return (
      <IconComponent
        size={20}
        color={iconColor}
      />
    );
  }

  // Match without icon prefix
  const normalized = iconName
    .toLowerCase()
    .replace(/^(fa|si|md|hi|ri|io|lu)/, "")
    .replace(/[^a-z0-9]/g, "");

  const matchedKey = Object.keys(icons).find(
    (key) =>
      key
        .toLowerCase()
        .replace(/^(fa|si|md|hi|ri|io|lu)/, "")
        .replace(/[^a-z0-9]/g, "") === normalized
  );

  if (matchedKey) {
    const IconComponent = icons[matchedKey];

    return (
      <IconComponent
        size={20}
        color={iconColor}
      />
    );
  }

  return null;
};

const getCardIcon = (iconName) => {
  const iconMap = {
    'user': icons.User,
    'Users': icons.Users,
    'award': icons.Award,
    'target': icons.Target,
    'sparkles': icons.Sparkles,
    'star': icons.Star,
    'book': icons.BookOpen,
    'message': icons.MessageCircle,
    'code': icons.Code,
    'wrench': icons.Wrench,
    'laptop': icons.Laptop,
    'lightbulb': icons.Lightbulb,
    'zap': icons.Zap,
    'Plane': icons.Plane
  };
  return iconMap[iconName] || icons.Users;
};

const getHighlightIcon = (iconName) => {
  const iconMap = {
    'users': icons.Users,
    'book': icons.BookOpen,
    'sparkles': icons.Sparkles,
    'message': icons.MessageCircle,
    'award': icons.Award,
    'target': icons.Target,
    'star': icons.Star,
    'code': icons.Code,
    'wrench': icons.Wrench,
    'laptop': icons.Laptop,
    'lightbulb': icons.Lightbulb,
    'zap': icons.Zap
  };
  return iconMap[iconName] || icons.Target;
};

const getCategoryColors = (category) => {
  const categoryLower = category?.toLowerCase() || '';
  // Full implementation from original – unchanged
  const base = {
    badge: 'bg-indigo-500/10 border-indigo-500/30',
    badgeText: 'text-indigo-400',
    badgeIcon: 'text-indigo-400',
    hover: 'hover:border-indigo-500/50',
    iconBg: 'from-indigo-500/20 to-blue-500/20',
    bullet: 'bg-indigo-500/20',
    bulletDot: 'bg-indigo-400',
    metric: 'text-indigo-400',
    organizerText: 'text-indigo-400'
  };
  return base;
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

// ==================== CARD COMPONENTS ====================
// All card components (ProjectCard, CommunityCard, PublicationCard) are kept exactly as provided.
// Only the "explore" cardDesign case is removed from the main Portfolio component.
// The card components themselves are unchanged because they don't contain explore‑specific logic.
// For brevity, they are reproduced here in full – but in practice you would import them from separate files.

// ===== ProjectCard =====
const ProjectCard = ({ project, index }) => {
  const cardRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const MAX_WORDS = 30;
  const words = project.description ? project.description.split(" ") : [];
  const needsReadMore = words.length > MAX_WORDS;
  const displayedText = isExpanded || !needsReadMore
    ? project.description
    : words.slice(0, MAX_WORDS).join(" ");
  const techStack = Array.isArray(project.techStack)
    ? project.techStack
    : typeof project.techStack === "string"
      ? project.techStack.split(",").map(t => t.trim()).filter(Boolean)
      : [];
  useEffect(() => {
    const node = cardRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
          entry.target.classList.remove('opacity-0', 'translate-y-10');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    if (node) observer.observe(node);
    return () => { if (node) observer.unobserve(node); };
  }, []);

  return (
    <div
      ref={cardRef}
      className="group bg-gradient-to-br from-slate-900/50 to-slate-800/50 backdrop-blur-xl rounded-2xl overflow-hidden border border-slate-700/50 transition-all duration-500 opacity-0 translate-y-10 hover:shadow-2xl hover:scale-[1.02] hover:border-indigo-500/50"
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {(project.image || project.imgSrc) && (
        <div className="relative overflow-hidden h-56 sm:h-64">
          <img
            src={project.image || project.imgSrc}
            alt={project.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
        </div>
      )}
      <div className="p-4 sm:p-6 lg:p-8">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-1.5 text-white group-hover:text-green-400 transition-colors duration-300">
          {project.title}
        </h3>
        {project.subtitle && (
          <h4 className="text-indigo-400 font-semibold text-sm sm:text-base mb-1">
            {project.subtitle}
          </h4>
        )}
        {project.subSubtitle && (
          <h5 className="text-cyan-400 text-xs sm:text-sm font-medium mb-1">
            {project.subSubtitle}
          </h5>
        )}
        {project.organizedBy && (
          <p className="text-slate-400 text-xs sm:text-sm mb-2">
            <span className="text-cyan-400 font-semibold">Organized by:</span> {project.organizedBy}
          </p>
        )}
        {project.description && (
          <p className="text-slate-400 leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base">
            {displayedText}
            {needsReadMore && !isExpanded && "..."}
            {needsReadMore && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="ml-1.5 sm:ml-2 text-slate-400 hover:text-slate-300 text-[10px] sm:text-sm transition-colors inline-block align-baseline"
              >
                {isExpanded ? "Show less" : "Read more"}
              </button>
            )}
          </p>
        )}
        {(project.status?.length ?? 0) > 0 && (
          <div className="grid grid-cols-3 gap-6 mb-4 bg-slate-900/60 rounded-xl px-6 py-4">
            {project.status.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="flex flex-col items-center text-center">
                  {Icon && <Icon className="w-5 h-5 text-cyan-400 mb-2" />}
                  <div className={`font-semibold leading-none text-white ${stat.size === "large" ? "text-xl" : stat.size === "small" ? "text-sm" : "text-lg"}`}>
                    {stat.value}
                  </div>
                  <div className="mt-1 text-sm text-slate-400">{stat.label}</div>
                </div>
              );
            })}
          </div>
        )}
        {(project.features?.length ?? 0) > 0 && (
          <div className="mb-6">
            <h4 className="flex items-center gap-2 text-cyan-400 font-semibold text-base sm:text-lg mb-2 sm:mb-3">
              <icons.Sparkles className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              Key Features
            </h4>
            <ul className="space-y-1.5 sm:space-y-2">
              {project.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2.5 text-slate-300 text-sm sm:text-base">
                  <span className="text-indigo-400 text-base sm:text-xl font-bold flex-shrink-0 leading-none">▹</span>
                  <span className="flex-1 leading-relaxed">{typeof feature === "object" ? feature?.text : feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {techStack.length > 0 && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-3">

              {techStack.slice(0, 3).map((tech, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-slate-800/50 rounded-md sm:rounded-lg border border-slate-700/50 transition-all duration-300 hover:bg-slate-700/50 hover:border-slate-600/50"
                >
                  <span className="text-base sm:text-lg shrink-0">
                    {getTechIcon(tech)}
                  </span>
                  <span className="text-slate-300 text-[11px] sm:text-xs font-medium truncate">
                    {typeof tech === "object" ? tech?.label : tech}
                  </span>
                </div>
              ))}

              {techStack.length > 3 && (
                <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-slate-800/50 rounded-md sm:rounded-lg border border-slate-700/50 text-slate-300 text-[11px] sm:text-xs font-medium group-hover:hidden group-active:hidden">
                  +{techStack.length - 3}
                </div>
              )}

              {techStack.slice(3).map((tech, idx) => (
                <div
                  key={idx}
                  className="hidden group-hover:flex group-active:flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-slate-800/50 rounded-md sm:rounded-lg border border-slate-700/50"
                >
                  <span className="text-base sm:text-lg shrink-0">
                    {getTechIcon(tech)}
                  </span>
                  <span className="text-slate-300 text-[11px] sm:text-xs font-medium truncate">
                    {typeof tech === "object" ? tech?.label : tech}
                  </span>
                </div>
              ))}

            </div>
          </div>
        )}
        {(project.github || project.demo) && (
          <div className="flex gap-3 mt-6">
            {project.demo && project.demo !== "#" && (
              <a href={project.demo} className="flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-800/50 rounded-md sm:rounded-lg border border-slate-600/50 text-slate-400 hover:border-cyan-500 active:border-cyan-400 hover:bg-cyan-900/20 active:bg-cyan-900/30 hover:text-cyan-300 active:text-cyan-200 transition-all duration-300 text-xs sm:text-sm font-medium active:scale-95 touch-manipulation" target="_blank" rel="noopener noreferrer">
                <icons.ExternalLink className="w-4 h-4" />
                Demo
              </a>
            )}
            {project.github && (
              <a href={project.github} className="flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-800/50 rounded-md sm:rounded-lg border border-slate-600/50 text-slate-400 hover:border-indigo-500 active:border-indigo-400 hover:bg-indigo-900/20 active:bg-indigo-900/30 hover:text-indigo-300 active:text-indigo-200 transition-all duration-300 text-xs sm:text-sm font-medium active:scale-95 touch-manipulation" target="_blank" rel="noopener noreferrer">
                <icons.Github className="w-4 h-4" />
                Code
              </a>
            )}
          </div>
        )}
        {project.duration && (
          <div className="flex items-center gap-1.5 text-slate-400 text-xs sm:text-sm mt-4 sm:mt-6">
            <icons.Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
            <span className="leading-none sm:leading-normal">{project.duration}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== CommunityCard =====
const CommunityCard = ({ item, index }) => {
  const CardIcon = getCardIcon(item.cardIcon);
  const colors = getCategoryColors(item.category);
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEndX(null);
    setTouchStartX(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };
  const onTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    const distance = touchStartX - touchEndX;
    if (distance > minSwipeDistance) nextImage();
    if (distance < -minSwipeDistance) prevImage();
  };
  const [isPdfListOpen, setIsPdfListOpen] = useState(false);

  const media =
    (item?.media?.length ? item.media :
      (item?.images?.length ? item.images :
        (item?.imgSrc ? [item.image || item.imgSrc] :
          (item?.pdf ? [item.pdf] :
            (item?.pdfs?.length ? item.pdfs :
              [])))));

  const getFileName = (file) => {
    if (!file) return "";
    const parts = file.split("/");
    const fullName = parts[parts.length - 1];
    return fullName.replace(".pdf", "");
  };

  const openImageModal = (index = 0) => {
    const file = media[index];
    if (isPdf(file)) {
      if (isMobile) { window.open(file, "_blank"); return; }
      setSelectedPdf(file);
      setIsPdfOpen(true);
    } else {
      setSelectedIndex(index);
      setIsImageOpen(true);
    }
  };

  const closeImageModal = () => setIsImageOpen(false);
  const nextImage = () => {
    if (!imageFiles.length) return;
    setSelectedIndex((prev) => prev === imageFiles.length - 1 ? 0 : prev + 1);
  };
  const prevImage = () => {
    if (!imageFiles.length) return;
    setSelectedIndex((prev) => prev === 0 ? imageFiles.length - 1 : prev - 1);
  };
  const isPdf = (url) => typeof url === "string" && url.toLowerCase().endsWith(".pdf");
  const imageFiles = media.filter((file) => !isPdf(file));
  const showPdfActions = item.showPdfActions !== false && selectedPdf && selectedPdf.toLowerCase().endsWith(".pdf");
  const pdfFiles = media.filter((file) => isPdf(file));

  const MAX_WORDS = 30;
  const descWords = item.description?.split(" ") || [];
  const descNeedsReadMore = descWords.length > MAX_WORDS;
  const displayedDesc = isDescExpanded || !descNeedsReadMore ? item.description : descWords.slice(0, MAX_WORDS).join(" ");
  const summaryWords = item.summary?.description?.split(" ") || [];
  const summaryNeedsReadMore = summaryWords.length > MAX_WORDS;
  const displayedSummary = isSummaryExpanded || !summaryNeedsReadMore ? item.summary?.description : summaryWords.slice(0, MAX_WORDS).join(" ");

  return (
    <div
      key={index}
      className={`bg-slate-900/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 w-full max-w-[520px] h-auto ${colors.hover} hover:-translate-y-1 hover:scale-[1.02] transition-all duration-300`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md bg-orange-500/90 border border-orange-600">
          <icons.Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
          <span className="text-white font-semibold text-[10px] sm:text-xs uppercase">{item.category}</span>
        </div>
        {item.subbadge && (
          <span className="px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-medium whitespace-nowrap upper">
            {item.subbadge}
          </span>
        )}
      </div>
      <div className="flex items-start gap-4 mb-6">
        <div className={`p-3 rounded-xl bg-gradient-to-br ${colors.iconBg}`}>
          <CardIcon className="w-8 h-8 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-1">{item.title}</h3>
          {item.subtitle && <p className="text-slate-400 text-xs sm:text-sm font-medium mb-1.5">{item.subtitle}</p>}
          {item.Subtitle && <p className="text-slate-400 text-xs sm:text-sm font-medium mb-1.5">{item.Subtitle}</p>}
          {item.organizedBy && (
            <p className="text-slate-400 text-xs sm:text-sm font-medium mb-2">
              <span className="text-blue-500">Organized by: </span>
              <span className={colors.organizerText}>{item.organizedBy}</span>
            </p>
          )}
          <p className="flex items-center gap-1.5 text-xs sm:text-sm">
            <icons.Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 flex-shrink-0" />
            <span className="text-orange-400 leading-none sm:leading-normal">{item.duration}</span>
          </p>
        </div>
      </div>
      {item.description && (
        <p className="text-slate-400 text-sm leading-relaxed mb-2">
          {displayedDesc}
          {descNeedsReadMore && !isDescExpanded && "..."}
          {descNeedsReadMore && (
            <button onClick={() => setIsDescExpanded(!isDescExpanded)} className="ml-1.5 sm:ml-2 text-slate-400 hover:text-slate-300 text-[10px] sm:text-sm transition-colors inline-block align-baseline">
              {isDescExpanded ? "Show less" : "Read more"}
            </button>
          )}
        </p>
      )}
      {(item.features?.length ?? 0) > 0 && (
        <div className="space-y-3 mb-8">
          {item.features.map((highlight, i) => {
            const isObjectHighlight = typeof highlight === 'object';
            const HighlightIcon = isObjectHighlight ? getHighlightIcon(highlight.icon) : null;
            const highlightText = isObjectHighlight ? highlight.text : highlight;
            return (
              <div key={i} className="flex items-start gap-2 sm:gap-3">
                {isObjectHighlight ? (
                  <div className="p-1 rounded-md sm:rounded-lg flex items-center justify-center">
                    <HighlightIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${colors.badgeIcon}`} />
                  </div>
                ) : (
                  <div className={`p-1 rounded-full ${colors.bullet} flex items-center justify-center`}>
                    <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${colors.bulletDot}`} />
                  </div>
                )}
                <p className="text-white text-xs sm:text-sm leading-snug sm:leading-normal">{highlightText}</p>
              </div>
            );
          })}
        </div>
      )}
      {media.length > 0 && (
        <div className="flex gap-6 mt-4 mb-6 items-center flex-wrap">
          {imageFiles.length > 0 && (
            <div className="relative w-28 h-20 cursor-pointer" onClick={() => openImageModal(media.indexOf(imageFiles[0]))}>
              <img src={imageFiles[0]} alt="Poster" className="w-full h-full object-cover rounded-lg" />
              {imageFiles.length > 1 && (
                <div className="absolute inset-0 bg-black/60 rounded-lg flex flex-col items-center justify-center gap-1">
                  <icons.Image className="w-5 h-5 text-white" />
                  <span className="text-white text-sm font-bold">+{imageFiles.length - 1}</span>
                </div>
              )}
            </div>
          )}
          {pdfFiles.length > 0 && (
            <div className="relative w-28 h-20 bg-slate-800 rounded-lg flex items-center justify-center cursor-pointer" onClick={() => setIsPdfListOpen(true)}>
              <icons.FileText className="w-8 h-8 text-red-500" />
              {pdfFiles.length > 1 && (
                <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
                  <span className="text-white text-sm font-bold">+{pdfFiles.length - 1}</span>
                </div>
              )}
            </div>
          )}
          <Modal isOpen={isPdfListOpen} onRequestClose={() => setIsPdfListOpen(false)} className="fixed inset-0 z-[9999] flex items-center justify-center" overlayClassName="fixed inset-0 bg-black/80 z-[9998]">
            <div className="w-80 bg-slate-900 rounded-xl p-4 shadow-2xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-white font-semibold">PDF Files</h3>
                <button onClick={() => setIsPdfListOpen(false)} className="text-slate-400 hover:text-white"><icons.X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3">
                {pdfFiles.map((file, index) => (
                  <div key={index} className="bg-slate-800 hover:bg-slate-700 p-3 rounded-lg cursor-pointer flex items-center gap-2" onClick={() => {
                    if (isMobile) { window.open(file, "_blank"); return; }
                    setSelectedPdf(file);
                    setIsPdfListOpen(false);
                    setIsPdfOpen(true);
                  }}>
                    <icons.FileText className="w-5 h-5 text-red-500" />
                    <span className="text-sm text-white">{getFileName(file)}</span>
                  </div>
                ))}
              </div>
            </div>
          </Modal>
          <div>
            <h4 className="text-white font-semibold text-sm">{item.postername}</h4>
            <p className="text-slate-400 text-xs">{item.typename}</p>
          </div>
        </div>
      )}
      {/* PDF Preview Modal */}
      <Modal isOpen={isPdfOpen} onRequestClose={() => setIsPdfOpen(false)} className="fixed inset-0 flex items-center justify-center p-2 sm:p-4 z-[9999]" overlayClassName="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9998]">
        <div className="relative bg-slate-900 border border-slate-700 rounded-2xl w-full sm:max-w-4xl h-[98vh] sm:h-[95vh] flex flex-col shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-700 bg-slate-900 flex-shrink-0">
            <div className="flex items-center gap-2 sm:gap-3">
              <icons.FileText className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
              <h2 className="text-white text-base sm:text-xl font-bold">{getFileName(selectedPdf) || "PDF Preview"}</h2>
            </div>
            <button onClick={() => setIsPdfOpen(false)} className="p-1.5 sm:p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <icons.X className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400 hover:text-white" />
            </button>
          </div>
          {showPdfActions && (
            <div className="absolute top-3 right-14 sm:top-4 sm:right-16 z-50 flex gap-2">
              <a href={selectedPdf} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 sm:gap-2 px-2 py-1.5 sm:px-3 sm:py-2 bg-slate-900/90 hover:bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-cyan-300 text-xs sm:text-sm font-medium transition-all">
                <icons.ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Open in New Tab</span>
              </a>
              <a href={selectedPdf} download={getFileName(selectedPdf) + ".pdf"} className="flex items-center gap-1 sm:gap-2 px-2 py-1.5 sm:px-3 sm:py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/50 rounded-lg text-cyan-400 text-xs sm:text-sm font-medium transition-all">
                <icons.Download className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Download</span>
              </a>
            </div>
          )}
          <div className="flex-1 overflow-hidden bg-slate-950">
            {selectedPdf && <iframe src={`${selectedPdf}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`} className="w-full h-full" title="PDF Viewer" />}
          </div>
        </div>
      </Modal>
      {/* Image Modal */}
      <Modal isOpen={isImageOpen} onRequestClose={closeImageModal} className="fixed inset-0 z-[9999] flex items-center justify-center md:p-4" overlayClassName="fixed inset-0 bg-black/90 z-[9998]">
        <div className="w-full h-screen md:h-[85vh] md:max-w-6xl bg-slate-900 md:rounded-2xl md:border md:border-slate-700 shadow-2xl overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-slate-700 bg-slate-900">
            <h2 className="text-lg font-semibold text-white">Media</h2>
            <button onClick={closeImageModal} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <icons.X className="w-6 h-6 text-slate-400 hover:text-white" />
            </button>
          </div>
          <div className="flex flex-1 flex-col md:flex-row overflow-hidden">
            {imageFiles.length > 0 && (
              <div className="relative flex-1 bg-black flex items-center justify-center p-4 md:p-6 overflow-hidden" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
                {imageFiles.length > 1 && (
                  <button onClick={prevImage} className="absolute left-3 md:left-6 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full text-xl">‹</button>
                )}
                <img src={imageFiles[selectedIndex]} alt="Preview" className="max-h-full max-w-full object-contain md:rounded-lg transition duration-300" />
                {imageFiles.length > 1 && (
                  <button onClick={nextImage} className="absolute right-3 md:right-6 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full text-xl">›</button>
                )}
                {imageFiles.length > 1 && (
                  <div className="absolute bottom-4 bg-black/60 text-white text-xs px-3 py-1 rounded-full">{selectedIndex + 1} / {imageFiles.length}</div>
                )}
              </div>
            )}
            <div className="w-full md:w-80 lg:w-96 border-t md:border-t-0 md:border-l border-slate-700 bg-slate-900 p-4 md:p-6 overflow-y-auto">
              <h3 className="text-base font-semibold text-white">{item.postername}</h3>
              <p className="text-sm text-slate-400 mt-1">{item.typename}</p>
              <div className="mt-4 md:mt-6 text-sm text-slate-300 leading-relaxed">{item.description}</div>
            </div>
          </div>
        </div>
      </Modal>
      {item.summary && (
        <div className="pt-5 mt-5 border-t border-slate-700/40">
          {(!item.summary.position || item.summary.position === "impact") && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-white font-semibold text-sm">{item.summary.title}</p>
                {item.summary.value && (
                  <span className={`text-lg sm:text-xl font-bold ${colors.metric}`}>{item.summary.value}</span>
                )}
              </div>
              {item.summary?.description && (
                <p className="text-slate-400 text-xs leading-snug">
                  {displayedSummary}
                  {summaryNeedsReadMore && !isSummaryExpanded && "..."}
                  {summaryNeedsReadMore && (
                    <button onClick={() => setIsSummaryExpanded(!isSummaryExpanded)} className="ml-1.5 sm:ml-2 text-slate-400 hover:text-slate-300 text-[10px] sm:text-sm transition-colors inline-block align-baseline">
                      {isSummaryExpanded ? "Show less" : "Read more"}
                    </button>
                  )}
                </p>
              )}
            </>
          )}
          {item.summary.position === "stat-left" && (
            <div>
              <div className={`text-2xl sm:text-3xl font-bold ${colors.metric}`}>{item.summary.value}</div>
              <p className="text-slate-400 text-xs sm:text-sm mt-0.5">{item.summary.label}</p>
            </div>
          )}
          {item.summary.position === "stat-right" && (
            <div className="text-right">
              <div className={`text-3xl font-bold ${colors.metric}`}>{item.summary.value}</div>
              <p className="text-slate-400 text-sm mt-0.5">{item.summary.label}</p>
            </div>
          )}
        </div>
      )}
      {item.link && (
        <a href={item.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-800/70 border border-slate-600/70 text-[11px] text-cyan-300 hover:text-cyan-200 hover:border-cyan-400/70 hover:bg-slate-800 transition-all duration-200">
          <icons.ExternalLink className="w-3 h-3" />
          <span>View</span>
        </a>
      )}
    </div>
  );
};

// ===== PublicationCard =====
const PublicationCard = ({ item, index }) => {
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const MAX_WORDS = 30;

  const words = item.description?.split(" ") || [];
  const needsReadMore = words.length > MAX_WORDS;

  const displayedText =
    isExpanded || !needsReadMore
      ? item.description
      : words.slice(0, MAX_WORDS).join(" ");

  const getTypeBadge = () => {
    if (item.conference) {
      return { icon: icons.FileText, label: 'Conference', value: item.conference };
    } else if (item.journal) {
      return { icon: icons.BookOpen, label: 'Journal', value: item.journal };
    } else if (item.preprint) {
      return { icon: icons.FileCode, label: 'Preprint', value: item.preprint };
    }
    return null;
  };

  const typeBadge = getTypeBadge();
  const imageSrc = item.image || item.imgSrc;
  return (
    <div
      key={index}
      className="group bg-gradient-to-br from-slate-900/50 to-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 overflow-hidden transition-all duration-300 hover:border-cyan-400/50 hover:shadow-[0_0_40px_rgba(0,180,255,0.35)] hover:-translate-y-1 hover:scale-[1.02] flex flex-col h-full w-full"
    >
      <div className="relative p-10 overflow-hidden bg-[linear-gradient(135deg,rgb(16,185,129),rgb(59,130,246))]">
        <div className="absolute inset-0 bg-white/10 backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        <div className="absolute top-4 right-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-orange-500/90 border border-orange-600">
          <span className="text-white font-semibold text-xs uppercase">
            {item.year}
          </span>
        </div>

        {imageSrc && (
          <div className="flex justify-center items-center h-full relative z-10">
            <img
              src={imageSrc}
              alt={item.title}
              className="w-20 h-20 object-contain opacity-80"
            />
          </div>
        )}
      </div>

      <div className="p-6 flex-1 flex flex-col">
        <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3 leading-tight">
          {item.title}
        </h3>

        {typeBadge && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className={`inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg border ${typeBadge.label === 'Conference'
              ? 'bg-purple-500/10 border-purple-500/30'
              : typeBadge.label === 'Journal'
                ? 'bg-emerald-500/10 border-emerald-500/30'
                : 'bg-orange-500/10 border-orange-500/30'
              }`}>
              <typeBadge.icon className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${typeBadge.label === 'Conference'
                ? 'text-purple-400'
                : typeBadge.label === 'Journal'
                  ? 'text-emerald-400'
                  : 'text-orange-400'
                }`} />
              <span className={`text-[10px] sm:text-xs font-semibold ${typeBadge.label === 'Conference'
                ? 'text-purple-400'
                : typeBadge.label === 'Journal'
                  ? 'text-emerald-400'
                  : 'text-orange-400'
                }`}>{typeBadge.label}</span>
            </div>
            <div className="inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1">
              <span className="text-cyan-300 text-xs sm:text-sm font-semibold">{typeBadge.value}</span>
            </div>
          </div>
        )}

        {item.description && (
          <p className="text-slate-400 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
            {displayedText}
            {needsReadMore && !isExpanded && "..."}
            {needsReadMore && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="ml-1.5 sm:ml-2 text-slate-400 hover:text-slate-300 text-[10px] sm:text-sm transition-colors inline-block align-baseline"
              >
                {isExpanded ? "Show less" : "Read more"}
              </button>
            )}
          </p>
        )}

        {Array.isArray(item.metrics) && item.metrics.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-5 bg-slate-900/60 rounded-xl p-5">
            {item.metrics.map((metric, idx) => (
              <div key={idx} className="text-center">
                <div
                  className={`font-bold text-indigo-500 leading-none ${metric.size === "large"
                    ? "text-2xl"
                    : metric.size === "small"
                      ? "text-sm"
                      : "text-xl"
                    }`}
                >
                  {metric.value}
                </div>
                <div className="mt-2 text-xs sm:text-sm text-slate-400 leading-snug">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>
        )}

        {(item.techStack?.length ?? 0) > 0 && (
          <div className="mb-4 mt-auto">
            <div className="flex flex-wrap gap-1.5 sm:gap-2">

              {item.techStack.slice(0, 2).map((tech, i) => {
                const iconName =
                  typeof tech === "object"
                    ? tech?.icon
                    : "";

                const label =
                  typeof tech === "object"
                    ? tech?.label || ""
                    : tech;

                const color =
                  typeof tech === "object"
                    ? tech?.color || "#94A3B8"
                    : "#94A3B8";

                const IconComponent =
                  iconName && icons[iconName]
                    ? icons[iconName]
                    : null;

                return (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-md sm:rounded-lg bg-slate-800/50 border border-slate-700/50 truncate">
                    {IconComponent && (
                      <IconComponent
                        size={16}
                        color={color}
                        className="shrink-0"
                      />
                    )}

                    <span className="text-slate-300">
                      {label}
                    </span>
                  </span>
                );
              })}

              {item.techStack.length > 2 && (
                <span
                  className="text-slate-300 text-[11px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-md sm:rounded-lg bg-slate-800/50 border border-slate-700/50 group-hover:hidden group-active:hidden"
                >
                  +{item.techStack.length - 2}
                </span>
              )}

              {item.techStack.slice(2).map((tech, i) => {
                const iconName =
                  typeof tech === "object"
                    ? tech?.icon
                    : "";

                const label =
                  typeof tech === "object"
                    ? tech?.label || ""
                    : tech;

                const color =
                  typeof tech === "object"
                    ? tech?.color || "#94A3B8"
                    : "#94A3B8";

                const IconComponent =
                  iconName && icons[iconName]
                    ? icons[iconName]
                    : null;

                return (
                  <span
                    key={`extra-${i}`}
                    className="hidden group-hover:inline-flex group-active:inline-flex items-center gap-1.5 text-[11px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-md sm:rounded-lg bg-slate-800/50 border border-slate-700/50 animate-in fade-in slide-in-from-bottom-1 duration-200 truncate"
                    style={{ color }}
                  >
                    {IconComponent && (
                      <IconComponent
                        size={16}
                        color={color}
                        className="shrink-0"
                      />
                    )}

                    <span className="text-slate-300">
                      {label}
                    </span>
                  </span>
                );
              })}

            </div>
          </div>
        )}

        {item.publisher && (
          <div className="pt-3 mt-2 flex items-center gap-3 text-xs text-slate-400">
            <p className="text-xs sm:text-sm leading-snug sm:leading-normal text-slate-400">
              <span className="font-semibold">Publisher:</span>{" "}
              <span>{item.publisher}</span>
            </p>

            {item.publisherLink && (
              <a
                href={item.publisherLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-800/70 border border-slate-600/70 text-[11px] text-cyan-300 hover:text-cyan-200 hover:border-cyan-400/70 hover:bg-slate-800 transition-all duration-200"
              >
                <icons.ExternalLink className="w-3 h-3" />
                <span>View</span>
              </a>
            )}
          </div>
        )}
        {item.authors && (
          <div className="mt-3">
            <p className="text-xs sm:text-sm text-slate-400">
              <span className="font-semibold text-slate-300">Authors:</span>{" "}
              {Array.isArray(item.authors)
                ? item.authors.join(", ")
                : item.authors}
            </p>
          </div>
        )}
      </div>

      {item.citation && (
        <div className="overflow-hidden max-h-0 opacity-0 group-hover:max-h-40 group-hover:opacity-100 transition-all duration-300 ease-out">
          <div className="border-t border-slate-700/50 p-4">
            <div className="flex items-start gap-2">
              <p className="inline-block text-slate-400 text-[11px] sm:text-xs leading-relaxed bg-slate-800/60 backdrop-blur-sm px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-md sm:rounded-lg flex-1">
                <span className="font-semibold text-slate-400">Citation:</span>{" "}
                {item.citation}
              </p>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(item.citation);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="flex-shrink-0 flex items-center gap-1 text-[11px] sm:text-xs px-2 py-1.5 rounded-md bg-slate-800/60 text-slate-400 hover:text-slate-200 hover:bg-slate-700/60 transition-all duration-150"
              >
                {copied ? (
                  <>
                    <icons.Check className="w-3 h-3 text-green-400" />
                    <span className="text-green-400">Copied!</span>
                  </>
                ) : (
                  <>
                    <icons.Copy className="w-3 h-3" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const ServiceCard = ({ service, index }) => {
  const serviceIcons = {
    Globe: icons.Globe,
    Smartphone: icons.Smartphone,
    Palette: icons.Palette,
    Search: icons.Search,
    Megaphone: icons.Megaphone,
    Code2: icons.Code2,
    Server: icons.Server,
    Cloud: icons.Cloud,
    ShieldCheck: icons.ShieldCheck,
    Wifi: icons.Wifi,
  };

  // Same keys/structure as getCategoryColors() in CommunityCard
  const colorClasses = {
    indigo: {
      iconBg: "from-indigo-500 to-indigo-600",
      hover: "hover:border-indigo-500/40",
      bullet: "bg-indigo-500/10",
      bulletDot: "bg-indigo-400",
      badgeIcon: "text-indigo-400",
      metric: "text-indigo-400",
      organizerText: "text-indigo-400",
    },
    purple: {
      iconBg: "from-purple-500 to-purple-600",
      hover: "hover:border-purple-500/40",
      bullet: "bg-purple-500/10",
      bulletDot: "bg-purple-400",
      badgeIcon: "text-purple-400",
      metric: "text-purple-400",
      organizerText: "text-purple-400",
    },
    cyan: {
      iconBg: "from-cyan-500 to-cyan-600",
      hover: "hover:border-cyan-500/40",
      bullet: "bg-cyan-500/10",
      bulletDot: "bg-cyan-400",
      badgeIcon: "text-cyan-400",
      metric: "text-cyan-400",
      organizerText: "text-cyan-400",
    },
    emerald: {
      iconBg: "from-emerald-500 to-emerald-600",
      hover: "hover:border-emerald-500/40",
      bullet: "bg-emerald-500/10",
      bulletDot: "bg-emerald-400",
      badgeIcon: "text-emerald-400",
      metric: "text-emerald-400",
      organizerText: "text-emerald-400",
    },
    amber: {
      iconBg: "from-amber-500 to-amber-600",
      hover: "hover:border-amber-500/40",
      bullet: "bg-amber-500/10",
      bulletDot: "bg-amber-400",
      badgeIcon: "text-amber-400",
      metric: "text-amber-400",
      organizerText: "text-amber-400",
    },
    rose: {
      iconBg: "from-rose-500 to-rose-600",
      hover: "hover:border-rose-500/40",
      bullet: "bg-rose-500/10",
      bulletDot: "bg-rose-400",
      badgeIcon: "text-rose-400",
      metric: "text-rose-400",
      organizerText: "text-rose-400",
    },
  };

  const Icon = serviceIcons[service.icon] || icons.Globe;
  const colors = colorClasses[service.color] || colorClasses.indigo;

  const [isDescExpanded, setIsDescExpanded] = useState(false);

  const MAX_WORDS = 30;
  const descWords = service.description?.split(" ") || [];
  const descNeedsReadMore = descWords.length > MAX_WORDS;
  const displayedDesc =
    isDescExpanded || !descNeedsReadMore
      ? service.description
      : descWords.slice(0, MAX_WORDS).join(" ");

  return (
    <div
      key={index}
      style={{ animationDelay: `${index * 100}ms` }}
      className={`
        bg-slate-900/50
        backdrop-blur-sm
        rounded-2xl
        border
        border-slate-700/50
        p-6
        w-full
        max-w-[520px]
        h-auto
        ${colors.hover}
        hover:-translate-y-1
        hover:scale-[1.02]
        transition-all
        duration-300
      `}
    >
      {/* BADGES */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md bg-orange-500/90 border border-orange-600">
          <icons.Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
          <span className="text-white font-semibold text-[10px] sm:text-xs uppercase">
            {service.category || "Service"}
          </span>
        </div>
        {service.subbadge && (
          <span className="px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-medium whitespace-nowrap upper">
            {service.subbadge}
          </span>
        )}
      </div>

      {/* HEADER: icon + title + meta */}
      <div className="flex items-start gap-4 mb-6">
        <div className={`p-3 rounded-xl bg-gradient-to-br ${colors.iconBg}`}>
          <Icon className="w-8 h-8 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-1">
            {service.title}
          </h3>

          {service.subtitle && (
            <p className="text-slate-400 text-xs sm:text-sm font-medium mb-1.5">
              {service.subtitle}
            </p>
          )}
          {service.Subtitle && (
            <p className="text-slate-400 text-xs sm:text-sm font-medium mb-1.5">
              {service.Subtitle}
            </p>
          )}

          {service.organizedBy && (
            <p className="text-slate-400 text-xs sm:text-sm font-medium mb-2">
              <span className="text-blue-500">Provided by: </span>
              <span className={colors.organizerText}>{service.organizedBy}</span>
            </p>
          )}

          {service.duration && (
            <p className="flex items-center gap-1.5 text-xs sm:text-sm">
              <icons.Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 flex-shrink-0" />
              <span className="text-orange-400 leading-none sm:leading-normal">
                {service.duration}
              </span>
            </p>
          )}
        </div>
      </div>

      {/* DESCRIPTION */}
      {service.description && (
        <p className="text-slate-400 text-sm leading-relaxed mb-2">
          {displayedDesc}
          {descNeedsReadMore && !isDescExpanded && "..."}
          {descNeedsReadMore && (
            <button
              onClick={() => setIsDescExpanded(!isDescExpanded)}
              className="ml-1.5 sm:ml-2 text-slate-400 hover:text-slate-300 text-[10px] sm:text-sm transition-colors inline-block align-baseline"
            >
              {isDescExpanded ? "Show less" : "Read more"}
            </button>
          )}
        </p>
      )}

      {/* FEATURES */}
      {(service.features?.length ?? 0) > 0 && (
        <div className="space-y-3 mb-8">
          {service.features.map((feature, i) => {
            const isObjectFeature = typeof feature === "object";
            const FeatureIcon = isObjectFeature
              ? serviceIcons[feature.icon] || icons.Sparkles
              : null;
            const featureText = isObjectFeature ? feature.text : feature;

            return (
              <div key={i} className="flex items-start gap-2 sm:gap-3">
                {isObjectFeature ? (
                  <div className="p-1 rounded-md sm:rounded-lg flex items-center justify-center">
                    <FeatureIcon
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${colors.badgeIcon}`}
                    />
                  </div>
                ) : (
                  <div
                    className={`p-1 rounded-full ${colors.bullet} flex items-center justify-center`}
                  >
                    <div
                      className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${colors.bulletDot}`}
                    />
                  </div>
                )}
                <p className="text-white text-xs sm:text-sm leading-snug sm:leading-normal">
                  {featureText}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* SUMMARY */}
      {service.summary && (
        <div className="pt-5 mt-5 border-t border-slate-700/40">
          <div className="flex items-center justify-between">
            <p className="text-white font-semibold text-sm">{service.summary.title}</p>
            {service.summary.value && (
              <span className={`text-lg sm:text-xl font-bold ${colors.metric}`}>
                {service.summary.value}
              </span>
            )}
          </div>
          {service.summary.description && (
            <p className="text-slate-400 text-xs leading-snug mt-1">
              {service.summary.description}
            </p>
          )}
        </div>
      )}

      {/* LINK */}
      {service.link && (
        <a
          href={service.link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-4 px-2 py-1 rounded-full bg-slate-800/70 border border-slate-600/70 text-[11px] text-cyan-300 hover:text-cyan-200 hover:border-cyan-400/70 hover:bg-slate-800 transition-all duration-200"
        >
          <icons.ExternalLink className="w-3 h-3" />
          <span>View</span>
        </a>
      )}
    </div>
  );
};
// ==================== MAIN PORTFOLIO COMPONENT ====================
const Portfolio = () => {
  // State for each category (explore removed)
  const [projects, setProjects] = useState([]);
  const [publications, setPublications] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const isPaginationClick = useRef(false);

  // Fetch data from backend (no explore)
  useEffect(() => {
    const loadData = async () => {
      try {
        const [
          portfolioRes,
          publicationRes,
          communityRes,
          servicesRes
        ] = await Promise.all([
          getPortfolio(),
          getPublications(),
          getCommunity(),
          api.get("/services/active")
        ]);
        setProjects(portfolioRes.data.data || []);
        setPublications(publicationRes.data.data || []);
        setCommunities(communityRes.data.data || []);
        setServices(servicesRes.data?.data || []);
      } catch (err) {
        console.error("Failed to load portfolio data:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Build categories dynamically from separate states (explore removed)
  const portfolioCategories = useMemo(() => ({
    project: projects,
    publication: publications,
    community: communities,
    services: services,
  }), [projects, publications, communities, services]);

  // Compute filter categories with labels and counts
  const filterCategories = useMemo(() => {
    return Object.keys(portfolioCategories)
      .filter((key) => Array.isArray(portfolioCategories[key]) && portfolioCategories[key].length > 0)
      .map((key) => ({
        key,
        label:
          labelMap[key] ||
          key.charAt(0).toUpperCase() +
          key.slice(1).replace(/([A-Z])/g, " $1").trim(),
        count: portfolioCategories[key].length,
      }));
  }, [portfolioCategories]);

  // Active filter (no explore logic)
  const [activeFilter, setActiveFilter] = useState(() => {
    return Object.keys(portfolioCategories)[0] || 'project';
  });

  const isMobile = useIsMobile();
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = useMemo(() => (isMobile ? 4 : 6), [isMobile]);

  const currentItems = portfolioCategories[activeFilter] || [];
  const totalItems = currentItems.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const visibleItems = currentItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePageChange = (newPage) => {
    isPaginationClick.current = true;
    setCurrentPage(newPage);
  };

  // Scroll to top on page change
  useEffect(() => {
    if (!isPaginationClick.current) return;
    isPaginationClick.current = false;
    document.getElementById("projects")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [isMobile]);

  // Navbar filter click
  useEffect(() => {
    const handleNavbarClick = (e) => {
      if (e.detail?.section) {
        setActiveFilter(e.detail.section);
        setCurrentPage(1);
      }
    };
    window.addEventListener('navbarProjectsClick', handleNavbarClick);
    return () => window.removeEventListener('navbarProjectsClick', handleNavbarClick);
  }, []);

  // Location state filter
  useEffect(() => {
    const filterKey = location.state?.filter;
    if (!filterKey) return;
    const matchedKey = Object.keys(portfolioCategories).find(
      key => key.toLowerCase() === filterKey.toLowerCase()
    );
    if (matchedKey) {
      setActiveFilter(matchedKey);
      setCurrentPage(1);
    }
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, navigate, portfolioCategories]);

  // Grid class based on card design (removed explore/common cases)
  const getGridClass = () => {
    if (currentItems.length === 1) {
      const design = (visibleItems[0]?.cardDesign || visibleItems[0]?.cardType || 'project').toLowerCase();
      switch (design) {
        case 'project':
          return 'grid-cols-1 max-w-[420px] mx-auto';
        case 'community':
          return 'grid-cols-1 max-w-[520px] mx-auto';
        case 'publication':
          return 'grid-cols-1 max-w-[600px] mx-auto';
        default:
          return 'grid-cols-1 max-w-[520px] mx-auto';
      }
    }

    if (currentItems.length === 2) {
      const design = (visibleItems[0]?.cardDesign || visibleItems[0]?.cardType || 'project').toLowerCase();
      switch (design) {
        case 'project':
          return 'md:grid-cols-2 max-w-[900px] mx-auto';
        case 'community':
          return 'md:grid-cols-2 max-w-[1100px] mx-auto';
        case 'publication':
          return 'grid-cols-1 md:grid-cols-2 max-w-[1260px] mx-auto';
        default:
          return 'md:grid-cols-2 max-w-[1100px] mx-auto';
      }
    }

    // 3+ items
    const design = (visibleItems[0]?.cardDesign || visibleItems[0]?.cardType || 'project').toLowerCase();
    switch (design) {
      case 'project':
        return 'md:grid-cols-2 lg:grid-cols-3 max-w-[1320px] mx-auto';
      case 'community':
        return 'md:grid-cols-2 lg:grid-cols-3 max-w-[1680px] mx-auto';
      case 'publication':
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-[1920px] mx-auto';
      default:
        return 'md:grid-cols-2 lg:grid-cols-3 max-w-[1320px] mx-auto';
    }
  };

  // Section title (no explore)
  const getSectionTitle = () => {
    const filter = activeFilter || "project";
    const defaultTitle = {
      main: labelMap[filter] || filter.charAt(0).toUpperCase() + filter.slice(1),
      highlight: '',
      subtitle: `Explore my ${labelMap[filter]?.toLowerCase() || filter}`
    };
    return { ...defaultTitle, ...(sectionTitles[filter] || {}) };
  };
  const sectionTitle = getSectionTitle();

  // Filter visibility (no explore)
  const MAX_VISIBLE = 3;
  const [showAllFilters, setShowAllFilters] = useState(false);
  const filterRef = useRef(null);
  const cardsRef = useRef(null);

  const visibleFilters = useMemo(() => {
    if (showAllFilters) return filterCategories;
    const activeIndex = filterCategories.findIndex(f => f.key === activeFilter);
    if (activeIndex < MAX_VISIBLE) {
      return filterCategories.slice(0, MAX_VISIBLE);
    }
    return [
      ...filterCategories.slice(0, MAX_VISIBLE - 1),
      filterCategories[activeIndex]
    ];
  }, [showAllFilters, activeFilter, filterCategories]);

  const hiddenCount = filterCategories.length - MAX_VISIBLE;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!showAllFilters) return;
      if (filterRef.current?.contains(e.target) || cardsRef.current?.contains(e.target)) return;
      setShowAllFilters(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showAllFilters]);

  // Loading state
  if (error) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-[#0C121D]">
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-indigo-500/30 blur-xl"></div>
            <div className="relative h-14 w-14 animate-spin rounded-full border-[6px] border-slate-700 border-t-indigo-500"></div>
          </div>
        </div>
      </section>
    );
  }

  // if (error) {
  //   return (
  //     <div className="flex items-center justify-center min-h-[60vh] text-white">
  //       <div className="text-center">
  //         <icons.AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
  //         <h2 className="text-xl font-bold">Failed to load data</h2>
  //         <p className="text-slate-400 mt-2">Please refresh the page or try again later.</p>
  //       </div>
  //     </div>
  //   );
  // }

  // FilterButtons component (no explore navigation)
  const FilterButtons = () => {
    return (
      <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-8 sm:mb-5">
        {visibleFilters.map(category => (
          <button
            key={category.key}
            className={`relative inline-flex items-center gap-2 px-2 py-1 text-xs lg:text-sm rounded-xl font-semibold whitespace-nowrap transition-all duration-300 hover:scale-105 ${activeFilter === category.key
              ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/50'
              : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700/50'
              }`}
            onClick={() => {
              setActiveFilter(category.key);
              setCurrentPage(1);
              window.dispatchEvent(new CustomEvent("portfolioFilterChange", { detail: { filter: category.key } }));
            }}
          >
            {category.label}
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${activeFilter === category.key ? 'bg-white/20' : 'bg-slate-700/50'}`}>
              {category.count}
            </span>
          </button>
        ))}
        {!showAllFilters && hiddenCount > 0 && (
          <button
            onClick={() => setShowAllFilters(true)}
            className="px-3 py-1 text-xs lg:text-sm rounded-xl font-semibold bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 transition"
          >
            +{hiddenCount}
          </button>
        )}
      </div>
    );
  };

  // Render card based on cardDesign (explore fallback removed)
  const renderCard = (item, index) => {

    // IMPORTANT:
    // Anything inside the Services filter MUST use ServiceCard.
    if (activeFilter === "services") {
      return (
        <ServiceCard
          key={item.id || item._id || index}
          service={item}
          index={index}
        />
      );
    }

    let design = item.cardDesign || item.cardType;

    // Auto detect if backend doesn't send card type
    if (!design) {
      if (item.conference || item.journal || item.publisher) {
        design = "publication";
      } else if (
        item.organizedBy ||
        item.postername ||
        item.typename ||
        item.summary
      ) {
        design = "community";
      } else if (item.github || item.demo || item.techStack) {
        design = "project";
      } else {
        design = "project";
      }
    }

    design = design.toLowerCase().trim();

    switch (design) {
      case "project":
        return (
          <ProjectCard
            key={item.id || item._id || index}
            project={item}
            index={index}
          />
        );

      case "publication":
        return (
          <PublicationCard
            key={item.id || item._id || index}
            item={item}
            index={index}
          />
        );

      case "community":
        return (
          <CommunityCard
            key={item.id || item._id || index}
            item={item}
            index={index}
          />
        );

      case "service":
      case "services":
        return (
          <ServiceCard
            key={item.id || item._id || index}
            service={item}
            index={index}
          />
        );

      default:
        console.warn("Unknown card type:", design, item);
        return null;
    }
  };

  return (
    <div id="projects" className="relative bg-[#0C121D] px-4 sm:px-6 lg:px-8 overflow-hidden py-16 sm:py-20">
      <div className="max-w-full mx-auto px-0 sm:px-0 lg:px-0">
        <div ref={filterRef}>
          <FilterButtons />
        </div>
        <div className="mb-12 sm:mb-16 lg:mb-20">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 leading-tight sm:leading-tight px-4 sm:px-0">
              {sectionTitle.main}{' '}
              <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                {sectionTitle.highlight}
              </span>
            </h2>
            <p className="text-slate-400 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto leading-relaxed px-4 sm:px-0">
              {sectionTitle.subtitle}
            </p>
          </div>

          <div ref={cardsRef} className={`grid gap-6 lg:gap-8 justify-items-center ${getGridClass()}`}>
            {visibleItems.map((item, index) => renderCard(item, index))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-12 flex-wrap">
              <button
                onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-1 px-2 py-1 sm:px-3 rounded-lg text-xs sm:text-sm border border-slate-600 text-slate-300 transition-all duration-300 hover:border-indigo-400 hover:text-indigo-300 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <icons.ChevronLeft className={`w-4 h-4 ${currentPage !== 1 ? 'animate-arrow-left' : ''}`} />
                <span>Prev</span>
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                const isFirst = page === 1;
                const isLast = page === totalPages;
                const isCurrent = page === currentPage;
                const visibleMobile = isFirst || isLast || isCurrent;
                const tabletStart = Math.max(2, Math.min(currentPage - 1, totalPages - 3));
                const tabletEnd = Math.min(totalPages - 1, tabletStart + 2);
                const visibleTablet = isFirst || isLast || isCurrent || (page >= tabletStart && page <= tabletEnd);
                const desktopStart = Math.max(2, Math.min(currentPage - 3, totalPages - 6));
                const desktopEnd = Math.min(totalPages - 1, desktopStart + 5);
                const visibleDesktop = isFirst || isLast || isCurrent || (page >= desktopStart && page <= desktopEnd);

                let visClass = '';
                if (!visibleMobile) visClass += ' hidden';
                else visClass += ' flex';
                if (!visibleTablet) visClass += ' md:hidden';
                else visClass += ' md:flex';
                if (!visibleDesktop) visClass += ' lg:hidden';
                else visClass += ' lg:flex';

                return (
                  <React.Fragment key={page}>
                    {page === currentPage && currentPage > 2 && <span className="flex md:hidden px-1 text-slate-400 flex-shrink-0">...</span>}
                    {page === tabletStart && tabletStart > 2 && <span className="hidden md:flex lg:hidden px-1 text-slate-400 flex-shrink-0">...</span>}
                    {page === desktopStart && desktopStart > 2 && <span className="hidden lg:flex px-1 text-slate-400 flex-shrink-0">...</span>}

                    <button
                      onClick={() => handlePageChange(page)}
                      className={`${visClass} w-8 h-8 md:w-auto md:h-auto md:px-3 md:py-1 items-center justify-center rounded-lg text-sm font-semibold transition flex-shrink-0 ${currentPage === page
                        ? 'bg-indigo-600 text-white shadow-lg'
                        : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50'
                        }`}
                    >
                      {page}
                    </button>

                    {page === currentPage && currentPage < totalPages - 1 && <span className="flex md:hidden px-1 text-slate-400 flex-shrink-0">...</span>}
                    {page === tabletEnd && tabletEnd < totalPages - 1 && <span className="hidden md:flex lg:hidden px-1 text-slate-400 flex-shrink-0">...</span>}
                    {page === desktopEnd && desktopEnd < totalPages - 1 && <span className="hidden lg:flex px-1 text-slate-400 flex-shrink-0">...</span>}
                  </React.Fragment>
                );
              })}

              <button
                onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1 px-2 py-1 sm:px-3 rounded-lg text-xs sm:text-sm border border-slate-600 text-slate-300 transition-all duration-300 hover:border-indigo-400 hover:text-indigo-300 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span>Next</span>
                <icons.ChevronRight className={`w-3 h-3 sm:w-4 sm:h-4 ${currentPage !== totalPages ? 'animate-arrow-right' : ''}`} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Portfolio;